import type { Ref } from 'react';
import { forwardRef } from 'react';

import classNames from 'classnames';

import type { GridTableData } from '@uikit/components/GridTable';
import { GridTable } from '@uikit/components/GridTable';
import { Spinner } from '@uikit/components/Spinner';

import type { BaseCampaignLeaderBoardProps } from './BaseCampaignLeaderBoard';

interface BaseCampaignLeaderBoardDesktopProps<
  Data extends GridTableData = GridTableData,
  Meta = undefined,
> extends Pick<BaseCampaignLeaderBoardProps<Data, Meta>, 'columns' | 'currentUser' | 'items'> {
  showEmpty?: boolean;
  showLoader?: boolean;
  pagesLength?: number;
}

export const BaseCampaignLeaderBoardDesktop = forwardRef(
  <Data extends GridTableData = GridTableData, Meta = undefined>(
    {
      columns,
      currentUser,
      items,
      showEmpty,
      showLoader,
      pagesLength,
    }: BaseCampaignLeaderBoardDesktopProps<Data, Meta>,
    ref: Ref<HTMLDivElement>,
  ) => (
    <GridTable
      header={columns}
      className="overflow-y-auto flex-col rounded-t-2xl bg-neutral-50 shadow-card min-h-[212px] max-h-[604px] w-full story-prose-none flex"
      bodyRowClassName={({ item }) => {
        const isSelf =
          item?.user_id && currentUser?.user_id && item.user_id === currentUser.user_id;

        return classNames(
          'border-b flex-shrink-0 border-primary-300 min-h-[56px] text-lg leading-5 font-light',
          {
            'sticky top-11 bg-primary-50 -order-1 max-md:py-3 max-md:items-baseline': isSelf,
          },
        );
      }}
      headRowClassName="bg-primary-200 min-h-[44px] text-neutral-500 sticky -order-1 top-0 font-secondary font-normal text-2xl leading-[28px]"
      rowClassName="gap-2 px-2 md:gap-4 md:px-4 grid-cols-[115px_1fr_1fr] items-center"
      meta={
        {
          self: currentUser,
        } as Meta
      }
      data={items}
    >
      {showEmpty && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}
      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={pagesLength} className="m-auto py-3">
          <Spinner size="md" />
        </div>
      )}
    </GridTable>
  ),
);

BaseCampaignLeaderBoardDesktop.displayName = 'BaseCampaignLeaderBoardDesktop';
